import { useCallback, useContext, useEffect } from 'react';

import { PwaContext } from '../PwaProvider';

/**
 * Handles in-game PWA installation and syncs the result back to Unity.
 *
 * Addresses Unity through the `pwa.install` and `pwa.status` slots. Pass bridged
 * `addEventListener` / `removeEventListener` / `sendMessage` from
 * `useUnityBridge` so each game contract resolves them to its own build names.
 *
 * @param sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param sendGoogleBasicEvent - Deferred Google Analytics basic event.
 * @param updatePushLead - () => void () (function to update sharpstar lead function)
 * @param isLoaded - boolean - Whether the Unity/WebGL content has finished loading.
 * @param sendMessage - () => void () - Bridged Unity sendMessage.
 * @param addEventListener - () => void - Bridged Unity addEventListener.
 * @param removeEventListener - () => void - Bridged Unity removeEventListener.
 */
export const useGamePwaInstall = ({
    sendGameDesignEvent,
    sendGoogleBasicEvent,
    isLoaded,
    updatePushLead,
    sendMessage,
    addEventListener,
    removeEventListener,
}) => {
    const {
        pwaStatus,
        deferredPwaPrompt,
        showInstallPrompt,
        pwaPromptAttempt,
    } = useContext(PwaContext);

    useEffect(() => {
        if (!isLoaded || pwaPromptAttempt === 0) return;

        sendMessage('pwa.status', pwaStatus);
    }, [isLoaded, pwaPromptAttempt, pwaStatus, sendMessage]);

    const handlePwaInstall = useCallback(
        async (screenType) => {
            const callSource = `Game:${screenType}`;

            sendGameDesignEvent(`PWA:CallInstall:${callSource}`);
            sendGoogleBasicEvent(
                'pwa',
                `call_install_${callSource.toLowerCase()}`
            );

            const { gameGaEvent, gaEvent } = await showInstallPrompt({
                deferredPrompt: deferredPwaPrompt,
                updatePushLead,
            });

            sendGameDesignEvent(`${gameGaEvent}${callSource}`);
            sendGoogleBasicEvent(
                'pwa',
                `${gaEvent}${callSource.toLowerCase()}`
            );
        },
        [
            sendGameDesignEvent,
            sendGoogleBasicEvent,
            deferredPwaPrompt,
            showInstallPrompt,
            updatePushLead,
        ]
    );

    useEffect(() => {
        addEventListener('pwa.install', handlePwaInstall);

        return () => removeEventListener('pwa.install', handlePwaInstall);
    }, [addEventListener, removeEventListener, handlePwaInstall]);
};
